// v3.15-1204 COMP-011 — Bonus Engine admin.
//
// Configuration-driven: plans + rules editing, trigger form, event log.
// Bonuses flow into the existing Compensation Ledger and Payroll.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Gift, Plus, Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

// v3.18-1346 P2 — Team scope removed. Team-wide bonus contests are
// operated by picking the exact YYYY-MM / YYYY-QN period and passing
// the full employee_ids list at trigger time. The available bonus types
// intentionally exclude "team" until a dedicated team roll-up is built.
const BONUS_TYPES = ['production', 'monthly', 'quarterly'];

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : '—');

const NewRuleModal = ({ open, onClose, onSaved, planId }) => {
  const [bonusType, setBonusType]           = useState('monthly');
  const [qualPeriod, setQualPeriod]         = useState('monthly');
  const [threshold, setThreshold]           = useState('');
  const [fixed, setFixed]                   = useState('');
  const [percentage, setPercentage]         = useState('');
  const [effective, setEffective]           = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]                   = useState('');
  const [saving, setSaving]                 = useState(false);

  const submit = async () => {
    if (!fixed && !percentage) { toast.error('Provide fixed_amount OR percentage'); return; }
    setSaving(true);
    try {
      await api.post(`/bonus-engine/plans/${planId}/rules`, {
        bonus_type:            bonusType,
        qualification_period:  qualPeriod || undefined,
        production_threshold:  threshold ? Number(threshold) : undefined,
        fixed_amount:          fixed ? Number(fixed) : undefined,
        percentage:            percentage ? Number(percentage) : undefined,
        effective_date:        effective,
        notes:                 notes || undefined,
      });
      toast.success('Bonus rule added');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Add Bonus Rule</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Bonus Type</label>
          <select data-testid="rule-type" value={bonusType} onChange={(e) => setBonusType(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {BONUS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Qualification Period</label>
          <Input data-testid="rule-period" value={qualPeriod} onChange={(e) => setQualPeriod(e.target.value)} placeholder="monthly | quarterly | 2026-Q3 | 2026-07" className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Production Threshold</label>
          <Input data-testid="rule-threshold" type="number" step="0.01" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="Minimum production to qualify" className="mb-3" />
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Fixed Amount</label>
              <Input data-testid="rule-fixed" type="number" step="0.01" value={fixed} onChange={(e) => setFixed(e.target.value)} placeholder="$" />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Percentage</label>
              <Input data-testid="rule-pct" type="number" step="0.01" value={percentage} onChange={(e) => setPercentage(e.target.value)} placeholder="%" />
            </div>
          </div>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="rule-effective" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="rule-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" data-testid="rule-cancel" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="rule-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Rule'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TriggerModal = ({ open, onClose, onDone, plan }) => {
  const [ruleId, setRuleId]           = useState(plan?.rules?.[0]?.id || '');
  const [period, setPeriod]           = useState('');
  const [businessDate, setBusinessDate] = useState(new Date().toISOString().slice(0, 10));
  const [employeeIds, setEmployeeIds] = useState('');
  const [saving, setSaving]           = useState(false);

  const run = async () => {
    if (!ruleId || !period || !employeeIds) { toast.error('rule, period, and employee IDs are required'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/bonus-engine/trigger', {
        bonus_plan_id:         plan.id,
        bonus_rule_id:         ruleId,
        employee_ids:          employeeIds.split(',').map((s) => s.trim()).filter(Boolean),
        qualification_period:  period,
        business_date:         businessDate,
      });
      toast.success(`Bonus events: ${data.created_count} created, ${data.skipped_count} skipped`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Trigger failed'); }
    finally { setSaving(false); }
  };

  if (!plan) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Trigger Bonus — {plan.name}</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Rule</label>
          <select data-testid="trigger-rule" value={ruleId} onChange={(e) => setRuleId(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">— Select rule —</option>
            {(plan.rules || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.bonus_type} · {r.qualification_period || 'any'} · {r.fixed_amount ? fmtMoney(r.fixed_amount) : (r.percentage ? `${r.percentage}%` : '—')}
              </option>
            ))}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Qualification Period</label>
          <Input data-testid="trigger-period" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2026-07 or 2026-Q3" className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Business Date</label>
          <Input data-testid="trigger-date" type="date" value={businessDate} onChange={(e) => setBusinessDate(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Employee IDs (comma-separated)</label>
          <Input data-testid="trigger-employees" value={employeeIds} onChange={(e) => setEmployeeIds(e.target.value)} placeholder="uuid1,uuid2,..." />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" data-testid="trigger-cancel" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="trigger-run" onClick={run} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> Run</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NewPlanModal = ({ open, onClose, onSaved }) => {
  const [companyId, setCompanyId] = useState('');
  const [name, setName]           = useState('');
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);

  const submit = async () => {
    if (!companyId || !name) { toast.error('Company + Name are required'); return; }
    setSaving(true);
    try {
      await api.post('/bonus-engine/plans', {
        company_id: companyId, name, effective_date: effective, notes: notes || undefined,
      });
      toast.success('Bonus plan created');
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">New Bonus Plan</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Company ID</label>
          <Input data-testid="plan-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="UUID" className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Plan Name</label>
          <Input data-testid="plan-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Monthly Production Bonus" className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Effective Date</label>
          <Input data-testid="plan-effective" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="plan-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" data-testid="plan-cancel" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="plan-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function BonusEnginePage() {
  const [loading, setLoading]   = useState(true);
  const [plans, setPlans]       = useState([]);
  const [events, setEvents]     = useState([]);
  const [tab, setTab]           = useState('plans');
  const [error, setError]       = useState(null);
  const [newPlan, setNewPlan]   = useState(false);
  const [newRuleFor, setNewRuleFor] = useState(null);
  const [triggerFor, setTriggerFor] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, e] = await Promise.all([
        api.get('/bonus-engine/plans'),
        api.get('/bonus-engine/events', { params: { limit: 500 } }),
      ]);
      setPlans(p?.data?.items || []);
      setEvents(e?.data?.items || []);
    } catch (err) {
      if (err?.response?.status === 403) setError('Bonus administration is restricted to admins.');
      else toast.error('Failed to load bonus data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const delRule = async (planId, ruleId) => {
    try {
      await api.delete(`/bonus-engine/plans/${planId}/rules/${ruleId}`);
      toast.success('Rule removed'); fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="bonus-engine-page">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Gift className="w-6 h-6 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Bonus Engine</h1>
            <p className="text-sm text-slate-500">
              Configure Bonus Plans & Rules. Triggered bonuses become immutable
              Bonus Events, flow through the Compensation Ledger, and pay via
              existing Payroll.
            </p>
          </div>
        </div>
        <Button data-testid="new-plan-btn" onClick={() => setNewPlan(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> New Bonus Plan
        </Button>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl">{error}</div>
      ) : loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-slate-200">
            {['plans', 'events'].map((t) => (
              <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
                      className={
                        'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ' +
                        (tab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-800')
                      }>
                {t === 'plans' ? 'Plans & Rules' : 'Bonus Events'}
              </button>
            ))}
          </div>

          {tab === 'plans' ? (
            plans.length === 0 ? (
              <div className="p-10 text-center text-slate-500 bg-white border rounded-2xl" data-testid="plans-empty">
                No bonus plans yet. Create one to start configuring rules.
              </div>
            ) : (
              <div className="space-y-4">
                {plans.map((p) => (
                  <div key={p.id} data-testid={`plan-${p.id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-slate-800">{p.name}</h3>
                        <p className="text-xs text-slate-500">
                          Company {p.company_id?.slice(0, 8)}… · Effective {fmtDate(p.effective_date)} · Status {p.status}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" data-testid={`add-rule-${p.id}`} onClick={() => setNewRuleFor(p.id)} variant="outline">
                          <Plus className="w-4 h-4 mr-1" /> Add Rule
                        </Button>
                        <Button size="sm" data-testid={`trigger-${p.id}`} onClick={() => setTriggerFor(p)} disabled={!p.rules?.length}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <Play className="w-4 h-4 mr-1" /> Trigger
                        </Button>
                      </div>
                    </div>

                    {(p.rules || []).length === 0 ? (
                      <div className="mt-3 text-xs text-slate-400">No rules yet.</div>
                    ) : (
                      <table className="w-full text-sm mt-3">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                          <tr>
                            <th className="text-left px-3 py-2">Type</th>
                            <th className="text-left px-3 py-2">Period</th>
                            <th className="text-right px-3 py-2">Threshold</th>
                            <th className="text-right px-3 py-2">Fixed</th>
                            <th className="text-right px-3 py-2">%</th>
                            <th className="text-left px-3 py-2">Effective</th>
                            <th className="text-center px-3 py-2">Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.rules.map((r) => (
                            <tr key={r.id} data-testid={`rule-${r.id}`} className="border-t border-slate-100">
                              <td className="px-3 py-2 capitalize">{r.bonus_type}</td>
                              <td className="px-3 py-2 text-xs">{r.qualification_period || '—'}</td>
                              <td className="px-3 py-2 text-right">{r.production_threshold != null ? fmtMoney(r.production_threshold) : '—'}</td>
                              <td className="px-3 py-2 text-right">{r.fixed_amount != null ? fmtMoney(r.fixed_amount) : '—'}</td>
                              <td className="px-3 py-2 text-right">{r.percentage != null ? `${r.percentage}%` : '—'}</td>
                              <td className="px-3 py-2 text-xs">{fmtDate(r.effective_date)}</td>
                              <td className="px-3 py-2 text-center text-xs">{r.status}</td>
                              <td className="px-3 py-2 text-right">
                                <button data-testid={`del-rule-${r.id}`} onClick={() => delRule(p.id, r.id)}
                                        className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
              {events.length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="events-empty">No bonus events yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="events-table">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">Plan</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Period</th>
                      <th className="text-right px-3 py-2">Production</th>
                      <th className="text-right px-3 py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.business_date)}</td>
                        <td className="px-3 py-2">{e.employee_name || e.employee_id}</td>
                        <td className="px-3 py-2">{e.bonus_plan_name}</td>
                        <td className="px-3 py-2 capitalize">{e.bonus_type}</td>
                        <td className="px-3 py-2 text-xs">{e.qualification_period}</td>
                        <td className="px-3 py-2 text-right">{fmtMoney(e.production_snapshot)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtMoney(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      <NewPlanModal open={newPlan} onClose={() => setNewPlan(false)} onSaved={fetchAll} />
      <NewRuleModal open={!!newRuleFor} onClose={() => setNewRuleFor(null)} onSaved={fetchAll} planId={newRuleFor} />
      <TriggerModal open={!!triggerFor} onClose={() => setTriggerFor(null)} onDone={fetchAll} plan={triggerFor} />
    </div>
  );
}
